import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { MapPin, Phone, Mail, Clock, ExternalLink, Navigation, Instagram } from "lucide-react";

// Canonical Google Business Profile short link — opens the venue directly in
// the Google Maps app on iOS/Android when installed.
const MAPS_LINK = "https://maps.app.goo.gl/HeXkvt2C4ekgL4tt5";
const MAPS_QUERY = "Carpe Diem Art & Learning Centre, Godinho House, Gomes Waddo, Majorda, South Goa 403713, India";
const MAPS_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(MAPS_QUERY)}`;
const MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(MAPS_QUERY)}&output=embed`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 lg:py-24">
      <Toaster position="top-center" richColors />
      <div className="overline">Visit us</div>
      <h1 className="display text-5xl md:text-7xl mt-6">Find us in <em className="italic text-terra">Majorda.</em></h1>

      <div className="mt-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-6">
          <div>
            <div className="overline mb-3">Address</div>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noreferrer"
              className="flex items-start gap-3 text-base group hover:text-terra transition"
              data-testid="address-map-link"
            >
              <MapPin size={16} className="mt-1 text-sage"/>
              <div>
                Carpe Diem Art &amp; Learning Centre<br/>
                H. No. 81/2, Godinho House<br/>
                Gomes Waddo, Majorda<br/>
                South Goa, Goa · India 403713
                <span className="inline-flex items-center gap-1 ml-2 text-xs text-terra opacity-70 group-hover:opacity-100 transition"><ExternalLink size={12}/> Open in Maps</span>
              </div>
            </a>
          </div>
          <div>
            <div className="overline mb-3">Hours</div>
            <div className="flex items-start gap-3 text-base">
              <Clock size={16} className="mt-1 text-sage"/>
              <div>
                Wednesday to Sunday<br/>
                10:00 AM — 7:00 PM<br/>
                <span className="text-muted2 text-sm">Cafe last orders by 6:30 PM. Closed on public holidays.</span>
              </div>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Reach us</div>
            <div className="space-y-2 text-base">
              <a href="tel:+919822580152" className="flex items-center gap-3 hover:text-terra transition" data-testid="contact-phone-link"><Phone size={16} className="text-sage"/> +91 98225 80152 · WhatsApp</a>
              <a href="mailto:info@carpediemgoa.com" className="flex items-center gap-3 hover:text-terra transition" data-testid="contact-email-link"><Mail size={16} className="text-sage"/> info@carpediemgoa.com</a>
              <a href="https://instagram.com/carpediemgoa" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-terra transition" data-testid="contact-instagram-link"><Instagram size={16} className="text-sage"/> @carpediemgoa · Instagram</a>
            </div>
          </div>

          <div className="mt-8">
            <div className="aspect-[4/3] border border-soft overflow-hidden">
              <iframe
                title="Carpe Diem Goa map"
                src={MAPS_EMBED}
                className="w-full h-full"
                loading="lazy"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              <a href={MAPS_DIRECTIONS} target="_blank" rel="noreferrer" className="btn-primary" data-testid="get-directions-btn">
                <Navigation size={14}/> Get directions
              </a>
              <a href={MAPS_LINK} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="open-in-maps-btn">
                <ExternalLink size={14}/> Open in Google Maps
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="overline mb-3">Drop a note</div>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
                className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
                data-testid="contact-name"
              />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
                className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
                data-testid="contact-email"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}
                className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
                data-testid="contact-phone"
              />
              <input placeholder="Subject" value={form.subject} onChange={(e)=>setForm({...form,subject:e.target.value})}
                className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
                data-testid="contact-subject"
              />
            </div>
            <textarea required rows={6} placeholder="Your message" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})}
              className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
              data-testid="contact-message"
            />
            <button disabled={submitting} className="btn-primary mt-4" data-testid="contact-submit">
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
